import { useDropzone } from 'react-dropzone';
import { Box, LinearProgress, Typography } from '@mui/material';
import styles from '@widgets/registration/docs/ui/style.module.scss';
import { observer } from 'mobx-react-lite';
import { fileToScanDoc, getFileErrorMessage } from '@shared/utils/files';
import { notifications } from '@shared/utils/notifications';
import logger from 'js-logger';
import { useRegistrationDocsContext } from '@widgets/registrationWizard/store/RegistrationDocs/RegistrationDocsProvider';
import { useTranslation } from '@shared/utils/i18n';
import { AddScanDoc } from '@shared/types/osiAccountApplications';
import { ResponsesRequiredDocsResponse } from '@/shared/api/orval/models';

interface Props {
  doc: ResponsesRequiredDocsResponse;
}

export const Dropzone: React.FC<Props> = observer(({ doc }) => {
  const viewModel = useRegistrationDocsContext();
  const { t, fieldWithPrefix: fwp } = useTranslation();

  // Прогресс держим в viewModel, а не в локальном стейте: сжатие идёт вне рендера,
  // а индикатор должен пережить перерисовки списка документов.
  const progress = viewModel.compressionProgress?.docTypeCode === doc.code ? viewModel.compressionProgress : null;

  const onDropHandler = async (files: any) => {
    try {
      // Файлы обрабатываем ПОСЛЕДОВАТЕЛЬНО, а не через Promise.all: растеризация PDF держит
      // в памяти растр целой страницы (~25 МБ на 2200px), и параллельная обработка нескольких
      // файлов роняет вкладку на телефоне. Плюс при параллельной обработке индикатор прогресса
      // показывал бы вперемешку страницы разных документов.
      const scans: AddScanDoc[] = [];
      for (let i = 0; i < files.length; i += 1) {
        // doc.maxSize — лимит типа документа с бэка (в байтах): бюджет сжатия берётся
        // как минимум из него и клиентского лимита nginx.
        // eslint-disable-next-line no-await-in-loop
        const scan = await fileToScanDoc(files[i], doc.code, doc.maxSize, (page, total) =>
          viewModel.setCompressionProgress(doc.code ?? '', page, total)
        );
        scans.push(scan);
      }
      await Promise.all(
        scans.map(async (scan) => {
          await viewModel.uploadScan(scan);
        })
      );
    } catch (e) {
      logger.error(e);
      // Показываем конкретную причину (файл велик / не удалось сжать / битый PDF / 413 от nginx),
      // а не пустой notifications.error() — раньше пользователь видел просто «Ошибка».
      notifications.toast(getFileErrorMessage(e), 'error');
    } finally {
      // Индикатор надо гасить и при ошибке тоже, иначе он замрёт на последней странице.
      viewModel.resetCompressionProgress();
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: onDropHandler,
    accept: ['image/*', 'application/pdf']
  });

  return (
    <Box>
      <Box {...getRootProps({ onClick: open })} className={`${styles.dropzone} mb-2`}>
        <input {...getInputProps()} className="hidden" />
        <Typography variant="body1" align={'center'} color={'text.primary'}>
          {fwp(doc, 'name')}
          {!doc.isRequired && ` (${t('common:notRequired')})`}
        </Typography>
      </Box>
      {progress && (
        <Box className="mb-2">
          <Typography variant="caption" color={'text.secondary'}>
            {t('common:pdfCompressing', { page: progress.page, total: progress.total })}
          </Typography>
          <LinearProgress variant="determinate" value={(progress.page / progress.total) * 100} />
        </Box>
      )}
    </Box>
  );
});
